import type { GlobalFilters } from '@/domains/filters/filter.entity';
import type { StudentResponseEntity } from './student.entity';

export interface StudentFilters extends GlobalFilters {
  indicadorRiesgo?: string;
  risk_level?: string[];
  career_short_name?: string[];
  schedule?: string[];
  searchQuery?: string;
}

/**
 * Clase de dominio que encapsula la lógica de filtrado de estudiantes
 * Implementa encapsulación real y principios OOP
 */
export class StudentFilterDomain {
  private readonly filters: StudentFilters;
  private readonly applyGlobalFilters: boolean;

  constructor(filters: StudentFilters = {}, applyGlobalFilters = false) {
    this.filters = { ...filters };
    this.applyGlobalFilters = applyGlobalFilters;
  }

  /**
   * Aplica todos los filtros configurados a una lista de estudiantes
   */
  applyTo(students: StudentResponseEntity[]): StudentResponseEntity[] {
    if (!students.length) return [];

    return students.filter((student) => this.matchesAllCriteria(student));
  }

  /**
   * Verifica si un estudiante cumple con todos los criterios de filtrado
   */
  private matchesAllCriteria(student: StudentResponseEntity): boolean {
    return (
      this.matchesYear(student) &&
      this.matchesSemester(student) &&
      this.matchesRiskIndicator(student) &&
      this.matchesRiskLevel(student) &&
      this.matchesCareer(student) &&
      this.matchesSchedule(student) &&
      this.matchesSearchQuery(student)
    );
  }

  /**
   * Verifica filtro por año
   */
  private matchesYear(student: StudentResponseEntity): boolean {
    if (!this.applyGlobalFilters) {
      return true;
    }

    return (
      !this.filters.year || String(student.year) === String(this.filters.year)
    );
  }

  /**
   * Verifica filtro por semestre
   */
  private matchesSemester(_student: StudentResponseEntity): boolean {
    if (!this.applyGlobalFilters) {
      return true;
    }

    return !this.filters.semester || true; // Implementar lógica específica según el modelo
  }

  /**
   * Verifica filtro por indicador de riesgo
   */
  private matchesRiskIndicator(student: StudentResponseEntity): boolean {
    return (
      !this.filters.indicadorRiesgo ||
      student.risk_indicators.includes(this.filters.indicadorRiesgo)
    );
  }

  /**
   * Verifica filtro por nivel de riesgo
   */
  private matchesRiskLevel(student: StudentResponseEntity): boolean {
    return (
      !this.filters.risk_level ||
      this.filters.risk_level.includes(student.risk_level.toLowerCase())
    );
  }

  /**
   * Verifica filtro por carrera
   */
  private matchesCareer(student: StudentResponseEntity): boolean {
    return (
      !this.filters.career_short_name ||
      this.filters.career_short_name.includes(
        student.career_short_name.toLowerCase(),
      )
    );
  }

  /**
   * Verifica filtro por jornada (campo `schedule` en la entidad)
   */
  private matchesSchedule(student: StudentResponseEntity): boolean {
    return (
      !this.filters.schedule ||
      this.filters.schedule.includes(student.schedule)
    );
  }

  /**
   * Normaliza texto eliminando tildes y convirtiendo a minúsculas
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Verifica filtro por búsqueda de texto
   */
  private matchesSearchQuery(student: StudentResponseEntity): boolean {
    if (!this.filters.searchQuery) return true;

    const searchFields = [
      student.enrollment_number,
      student.full_run,
      student.first_name,
      student.last_name1,
      student.last_name2,
      student.career_short_name,
      student.risk_level,
    ];

    const normalizedSearchTerm = this.normalizeText(this.filters.searchQuery);
    return searchFields.some((field) =>
      this.normalizeText(String(field)).includes(normalizedSearchTerm),
    );
  }

  /**
   * Obtiene información sobre los filtros activos
   */
  getActiveFiltersInfo(): {
    hasActiveFilters: boolean;
    activeFiltersCount: number;
    filterTypes: string[];
  } {
    const activeFilters = Object.entries(this.filters).filter(
      ([_, value]) =>
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0),
    );

    return {
      hasActiveFilters: activeFilters.length > 0,
      activeFiltersCount: activeFilters.length,
      filterTypes: activeFilters.map(([key]) => key),
    };
  }

  /**
   * Crea una nueva instancia con filtros adicionales
   */
  withAdditionalFilters(
    additionalFilters: Partial<StudentFilters>,
  ): StudentFilterDomain {
    return new StudentFilterDomain(
      {
        ...this.filters,
        ...additionalFilters,
      },
      this.applyGlobalFilters,
    );
  }

  /**
   * Crea una nueva instancia sin un filtro específico
   */
  withoutFilter(filterKey: keyof StudentFilters): StudentFilterDomain {
    const newFilters = { ...this.filters };
    delete newFilters[filterKey];
    return new StudentFilterDomain(newFilters, this.applyGlobalFilters);
  }

  /**
   * Obtiene una copia de los filtros actuales
   */
  getFilters(): Readonly<StudentFilters> {
    return { ...this.filters };
  }
}

/**
 * Servicio de compatibilidad para mantener la API existente
 * @deprecated Usar StudentFilterDomain directamente
 */
export class StudentFilterService {
  static filterStudents(
    students: StudentResponseEntity[],
    filters: StudentFilters,
  ): StudentResponseEntity[] {
    const filterDomain = new StudentFilterDomain(filters, false);
    return filterDomain.applyTo(students);
  }
}
// export class StudentFilterService {
//   static filterStudents(
//     students: StudentResponseEntity[],
//     filters: StudentFilters,
//   ): StudentResponseEntity[] {
//     const filterDomain = new StudentFilterDomain(filters);
//     return filterDomain.applyTo(students);
//   }
// }
